import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Star, ArrowDown } from "lucide-react";
import LineReveal from "@/components/LineReveal";
import { HERO_VIDEO, HERO_POSTER } from "@/data/config";
import { trackEvent } from "@/hooks/useBusinessData";

export default function Hero({ config, ratingMeta }) {
  const containerRef = useRef(null);
  const videoRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"],
  });
  const videoScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const videoY = useTransform(scrollYProgress, [0, 1], ["0%", "12%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.55, 0.9]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "-15%"]);

  const scrollDown = () => {
    document
      .querySelector("#destinations")
      ?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      ref={containerRef}
      className="relative min-h-[100svh] w-full overflow-hidden bg-black"
      data-testid="hero-section"
      id="top"
    >
      {/* Video Layer — locked to ONE studio-atmosphere video, loops forever */}
      <motion.div
        style={{ scale: videoScale, y: videoY }}
        className="absolute inset-0 z-0"
      >
        {/* Authentic She Cuts poster sits below the video for instant paint */}
        <img
          src={HERO_POSTER}
          alt=""
          aria-hidden
          className="absolute inset-0 w-full h-full object-cover object-[70%_center]"
        />
        <video
          ref={videoRef}
          src={HERO_VIDEO}
          poster={HERO_POSTER}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          disablePictureInPicture
          controls={false}
          className="relative w-full h-full object-cover pointer-events-none"
          data-testid="hero-video"
        />
      </motion.div>

      {/* Overlay */}
      <motion.div
        style={{ opacity: overlayOpacity }}
        className="absolute inset-0 z-[1] bg-gradient-to-b from-black/50 via-black/30 to-black"
      />
      <div className="absolute inset-0 z-[1] bg-black/25" />

      {/* Grain */}
      <div className="grain absolute inset-0 z-[2]" />

      {/* Top marker */}
      <div className="absolute top-24 md:top-28 left-6 md:left-10 z-10 flex items-center gap-4">
        <div className="eyebrow text-white/70">
          <span className="inline-block w-8 h-px bg-white/60 align-middle mr-3" />
          EST. {config.established} · Midtown Atlanta
        </div>
      </div>

      <div className="absolute top-24 md:top-28 right-6 md:right-10 z-10 hidden md:flex items-center gap-3 text-white/80">
        <div className="flex">
          {[...Array(5)].map((_, i) => (
            <Star key={i} className="w-3.5 h-3.5 fill-current" style={{ color: "var(--sc-star)" }} />
          ))}
        </div>
        <span className="eyebrow text-white/80">
          {ratingMeta.rating ? ratingMeta.rating.toFixed(1) : "5.0"} · Verified {ratingMeta.platform || "Yelp"} Reviews
        </span>
      </div>

      {/* Content */}
      <motion.div
        style={{ y: contentY }}
        className="relative z-10 max-w-[1600px] mx-auto px-6 md:px-10 pt-40 md:pt-48 pb-24 md:pb-28 min-h-[100svh] flex flex-col justify-end"
      >
        <div className="max-w-5xl">
          <LineReveal
            lines={["She Cuts", "Barber Studio."]}
            className="font-serif text-[16vw] sm:text-[13vw] md:text-[10vw] lg:text-[9rem] leading-[0.9] tracking-[-0.02em] text-white"
            delay={0.35}
            duration={1.1}
            stagger={0.14}
          />

          <div className="mt-8 md:mt-10 max-w-2xl">
            <LineReveal
              lines={[
                "Precision meets peace.",
                "Twenty-five years of Atlanta craft.",
              ]}
              className="font-serif italic text-lg sm:text-xl md:text-2xl text-zinc-200 leading-snug"
              delay={1.05}
              duration={0.9}
              stagger={0.1}
            />
          </div>
        </div>

        <motion.div
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 1.6, duration: 0.9, ease: [0.7, 0, 0.2, 1] }}
          className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-4"
        >
          <Link
            to="/book"
            onClick={() => trackEvent("book_clicked", { source: "hero" })}
            data-testid="hero-book-now-button"
            className="btn-primary"
          >
            Book Your Appointment
          </Link>
          <a
            href={`tel:${config.phone_tel}`}
            onClick={() => trackEvent("call_clicked", { source: "hero" })}
            data-testid="hero-call-button"
            className="btn-ghost"
          >
            Call {config.phone}
          </a>
        </motion.div>

        {/* Bottom rail */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2.1, duration: 1 }}
          className="mt-16 md:mt-20 flex items-center justify-between border-t border-white/10 pt-6"
        >
          <button
            onClick={scrollDown}
            className="eyebrow flex items-center gap-3 text-white/60 hover:text-white transition"
            data-testid="hero-scroll-cue"
          >
            <ArrowDown className="w-3.5 h-3.5" strokeWidth={1.5} />
            Explore the studio
          </button>
          <div className="hidden md:flex items-center gap-6 eyebrow text-white/50">
            <span>01 / Cuts</span>
            <span>02 / Beards</span>
            <span>03 / Craft</span>
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
}
